import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import {
  Play, Music, Star, Baby, Sparkles, Volume2, Puzzle, Shapes,
  Download, Wifi, WifiOff
} from 'lucide-react';

const InfantToddlerSection = ({ childId, childName, ageGroup }) => {
  const { api } = useAuth();
  const [activities, setActivities] = useState({ videos: [], games: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('videos');
  const [offlineStatus, setOfflineStatus] = useState({ cachedVideos: 0 });

  useEffect(() => {
    fetchActivities();
    checkOfflineStatus();
  }, [ageGroup]);

  const fetchActivities = async () => {
    try {
      const response = await api.get(`/curriculum/infant-toddler?age_group=${ageGroup}`);
      setActivities(response.data);
    } catch (error) {
      console.error('Failed to fetch infant/toddler activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkOfflineStatus = () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'GET_OFFLINE_STATUS' });
      
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'OFFLINE_STATUS') {
          setOfflineStatus(event.data);
        }
      });
    }
  };

  const cacheForOffline = (activity) => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      const videoUrl = activity.content?.video_url;
      if (videoUrl) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_VIDEO',
          videoUrl,
          videoId: activity.id
        });
      }
    }
  };

  const getActivityIcon = (activity) => {
    const type = activity.activity_type;
    const content = activity.content || {};
    
    if (type === 'video') {
      if (content.category === 'music') return <Music className="w-6 h-6" />;
      return <Play className="w-6 h-6" />;
    }
    if (content.type === 'simple_tap') return <Sparkles className="w-6 h-6" />;
    if (content.type === 'puzzle') return <Puzzle className="w-6 h-6" />;
    if (content.game === 'shape_sorter') return <Shapes className="w-6 h-6" />;
    return <Star className="w-6 h-6" />;
  };

  const getActivityGradient = (index) => {
    const gradients = [
      'from-pink-300 to-rose-400',
      'from-purple-300 to-indigo-400',
      'from-blue-300 to-cyan-400',
      'from-teal-300 to-teal-400',
      'from-amber-300 to-orange-400',
      'from-red-300 to-pink-400',
    ];
    return gradients[index % gradients.length];
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-pink-50 to-purple-50 border-0 overflow-hidden">
        <CardContent className="p-8 text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          >
            <Baby className="w-12 h-12 text-pink-400 mx-auto" />
          </motion.div>
          <p className="text-pink-600 mt-4">Loading activities...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 border-0 overflow-hidden shadow-lg">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-purple-500 rounded-2xl flex items-center justify-center shadow-md">
              <Baby className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-fredoka text-xl text-slate-800">
                {ageGroup === 'infant' ? '👶 Baby Time!' : '🧸 Toddler Fun!'}
              </h2>
              <p className="text-sm text-slate-500">
                {ageGroup === 'infant' ? 'Videos & sensory play for ' : 'Songs & games for '}{childName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {offlineStatus.cachedVideos > 0 && (
              <span className="text-xs text-teal-600 bg-teal-100 px-2 py-1 rounded-full flex items-center gap-1">
                <Wifi className="w-3 h-3" /> {offlineStatus.cachedVideos} saved
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={activeTab === 'videos' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('videos')}
            className={activeTab === 'videos' ? 'bg-pink-500 hover:bg-pink-600' : ''}
          >
            <Play className="w-4 h-4 mr-1" /> Videos ({activities.videos?.length || 0})
          </Button>
          <Button
            variant={activeTab === 'games' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('games')}
            className={activeTab === 'games' ? 'bg-purple-500 hover:bg-purple-600' : ''}
          >
            <Sparkles className="w-4 h-4 mr-1" /> Games ({activities.games?.length || 0})
          </Button>
        </div>

        {/* Activity Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {(activeTab === 'videos' ? activities.videos : activities.games)?.slice(0, 8).map((activity, index) => (
            <motion.div
              key={activity.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <Link to={`/activity/${activity.id}?child=${childId}`}>
                <Card className="overflow-hidden cursor-pointer border-0 shadow-md hover:shadow-lg transition-all group">
                  <div className={`h-20 bg-gradient-to-br ${getActivityGradient(index)} flex items-center justify-center text-white relative`}>
                    {getActivityIcon(activity)}
                    {activity.activity_type === 'video' && (
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Play className="w-10 h-10 fill-white" />
                      </div>
                    )}
                    {/* Download for offline button */}
                    <button
                      onClick={(e) => { e.preventDefault(); cacheForOffline(activity); }}
                      className="absolute top-1 right-1 w-6 h-6 bg-white/30 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Save for offline"
                    >
                      <Download className="w-3 h-3" />
                    </button>
                  </div>
                  <CardContent className="p-2">
                    <h3 className="font-semibold text-xs text-slate-700 line-clamp-2 leading-tight">
                      {activity.title}
                    </h3>
                    {activity.content?.duration_minutes && (
                      <p className="text-xs text-slate-400 mt-1">
                        {activity.content.duration_minutes} min
                      </p>
                    )}
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        <div className="mt-4 text-center">
          <Link to={`/learn/${childId}?category=infant-toddler`}>
            <Button variant="outline" className="border-pink-300 text-pink-600 hover:bg-pink-50">
              View All {activeTab === 'videos' ? 'Videos' : 'Games'} →
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

export default InfantToddlerSection;
